// Sauna Guy — client bootstrap. Wires the renderer, world, controller,
// interaction, routine, HUD, and the creepypasta manager into one loop.

import THREE from "./three.js";
import { Hud } from "./hud.js";
import { Controller } from "./controls.js";
import { InteractSystem } from "./interact.js";
import { Routine } from "./clock.js";
import { buildWorld } from "./world/layout.js";
import { EntityManager } from "./entities/manager.js";
import { Spectator } from "./spectator.js";
import { GameAudio } from "./audio.js";
import type { Seat } from "./world/layout.js";

const FLATLINES = [
  "The benches stayed warm a while.",
  "Nobody came to the next session.",
  "The watch kept ticking on your wrist.",
  "Steam closed over the place like nothing had happened.",
];

function main() {
  const app = document.getElementById("app")!;
  const hud = new Hud();

  // ── Renderer / scene / camera ──
  const renderer = new THREE.WebGLRenderer({ antialias: false });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  app.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x06060a);
  scene.fog = new THREE.FogExp2(0x07070c, 0.1);

  const camera = new THREE.PerspectiveCamera(
    72,
    window.innerWidth / window.innerHeight,
    0.05,
    100,
  );

  const world = buildWorld();
  scene.add(world.root);

  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  // ── Mode select ──────────────────────────────────────────────────────────
  // Default is SPECTATOR — open the page and watch an agent play (the Backrooms
  // experience). Add ?play=1 to play it yourself; ?watch=<id> follows a specific
  // agent's session instead of the resident demo.
  const params = new URLSearchParams(location.search);
  if (params.get("play") === null) {
    new Spectator(renderer, scene, camera, world, hud, params.get("watch")).start();
    return;
  }

  const controller = new Controller(camera, renderer.domElement, world.colliders);
  controller.spawn(world.spawn.pos, world.spawn.yaw);
  // Debug hook for automated collision QA.
  (window as unknown as { __pos: () => unknown }).__pos = () => {
    const p = controller.position();
    return { x: p.x, y: p.y, z: p.z };
  };

  const interact = new InteractSystem();
  const routine = new Routine();
  const audio = new GameAudio();
  audio.armOnFirstGesture();

  let state: "start" | "playing" | "dead" | "won" = "start";

  const BASE_FOV = 72;
  // Drop all perturbing effects back to neutral (on death / win / reset).
  function clearEffects() {
    controller.invert = false;
    controller.timeScale = 1;
    controller.fxRoll = controller.fxJx = controller.fxJy = 0;
    if (camera.fov !== BASE_FOV) { camera.fov = BASE_FOV; camera.updateProjectionMatrix(); }
    audio.setWhispers(false);
  }

  const manager = new EntityManager(scene, camera, world, controller, hud, routine, {
    onDeath: (reason) => die(reason),
    onReward: () => {},
  });

  // ── Interactables: doors + shower valve ──
  for (const key of ["dry", "steam", "exit"] as const) {
    const door = world.doors[key];
    interact.add({
      object: door.group,
      range: 2.0,
      label: () => {
        if (key === "exit" && !routine.canLeave()) return "The reception door won't open yet";
        return door.isOpen ? "Close door" : "Open door";
      },
      onUse: () => {
        if (key === "exit" && !routine.canLeave()) {
          hud.toastMsg("It's locked from the inside. Finish first.");
          return;
        }
        door.toggle();
        if (door.isOpen) manager.notifyDoorOpened(key);
      },
    });
  }
  interact.add({
    object: world.shower.valve,
    range: 2.0,
    label: () => (world.shower.isOn ? "Turn off the shower" : "Turn on the cold shower"),
    onUse: () => {
      world.shower.setOn(!world.shower.isOn);
      if (world.shower.isOn) {
        manager.notifyShowerOn();
        hud.toastMsg("Cold water hisses against the tiles.");
      }
    },
  });

  // ── Sit / stand (F), nearest seat ──
  function nearestSeat(): Seat | null {
    const p = controller.position();
    let best: Seat | null = null;
    let bestD = 1.3 * 1.3;
    for (const s of world.seats) {
      const dx = s.pos.x - p.x;
      const dz = s.pos.z - p.z;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  }
  function toggleSit() {
    if (controller.isSeated) {
      controller.stand();
      return;
    }
    const s = nearestSeat();
    if (s) controller.sit(s);
    else hud.toastMsg("Nothing to sit on here.");
  }

  // ── Input ──
  document.addEventListener("keydown", (e) => {
    if (state !== "playing") return;
    if (e.code === "KeyE") interact.use();
    else if (e.code === "KeyF") toggleSit();
    else if (e.code === "Tab") {
      e.preventDefault();
      hud.setWatchUp(true);
    } else if (e.code === "Digit1" || e.code === "Digit2" || e.code === "Digit3") {
      if (hud.dialogueOpen) hud.chooseByIndex(Number(e.code.slice(-1)) - 1);
    }
  });
  document.addEventListener("keyup", (e) => {
    if (e.code === "Tab") hud.setWatchUp(false);
  });

  renderer.domElement.addEventListener("click", () => {
    if (state === "playing" && !controller.locked) controller.requestLock();
  });

  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  // ── State transitions ──
  function startGame() {
    resetWorldState();
    state = "playing";
    hud.hideScreen("screen-start");
    hud.hideScreen("screen-dead");
    hud.hideScreen("screen-win");
    controller.setEnabled(true);
    controller.requestLock();
    hud.setCrosshair(true);
  }
  function die(reason: string) {
    if (state !== "playing") return;
    state = "dead";
    clearEffects();
    audio.deathStinger();
    controller.setEnabled(false);
    document.exitPointerLock?.();
    hud.setCrosshair(false);
    hud.hidePrompt();
    hud.showDead(reason, FLATLINES[Math.floor(Math.random() * FLATLINES.length)]);
  }
  function win() {
    if (state !== "playing") return;
    state = "won";
    clearEffects();
    routine.markDone();
    controller.setEnabled(false);
    document.exitPointerLock?.();
    hud.setCrosshair(false);
    hud.hidePrompt();
    hud.setDread(0);
    hud.showWin(`You left at ${routine.clockString()}.`);
  }
  function resetWorldState() {
    routine.phase = "dry";
    routine.gameMinutes = 20 * 60;
    routine.dry = routine.steam = routine.cool = 0;
    for (const k of ["dry", "steam", "exit"] as const) {
      if (world.doors[k].isOpen) world.doors[k].toggle();
    }
    world.shower.setOn(false);
    manager.reset();
    clearEffects();
    controller.spawn(world.spawn.pos, world.spawn.yaw);
  }

  hud.onClick("btn-start", startGame);
  hud.onClick("btn-retry", startGame);
  hud.onClick("btn-again", startGame);

  // ── Main loop ──
  let last = performance.now();
  function frame(now: number) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    if (state === "playing") {
      controller.update(dt);
      interact.update(camera);

      const pos = controller.position();
      const room = world.roomAt(pos.x, pos.z);
      routine.update(dt, room, controller.isSeated);
      manager.update(dt, now / 1000);

      // Perturbing effects: while a presence bears down (high dread) the room
      // turns on you — the look axes invert, or the view warps and whispers.
      const eff = manager.currentEffect();
      controller.invert = eff === "invert";
      const hallu = eff === "hallucinate";
      controller.fxRoll = hallu ? Math.sin(now * 0.0013) * 0.05 : 0;
      const j = hallu ? 0.012 * (0.4 + manager.dread) : 0;
      controller.fxJx = j ? (Math.sin(now * 0.043) + Math.sin(now * 0.071)) * j : 0;
      controller.fxJy = j ? (Math.sin(now * 0.037) + Math.sin(now * 0.067)) * j : 0;
      const wantFov = hallu ? BASE_FOV * (1 + Math.sin(now * 0.0017) * 0.06) : BASE_FOV;
      if (Math.abs(camera.fov - wantFov) > 0.01) { camera.fov = wantFov; camera.updateProjectionMatrix(); }
      audio.setDread(manager.dread);
      audio.setWhispers(eff === "voices");

      // Interaction prompt + crosshair heat
      const cur = interact.current;
      if (cur) {
        hud.showPrompt(cur.label());
        hud.setCrosshair(true, true);
      } else {
        hud.hidePrompt();
        hud.setCrosshair(true, false);
      }

      // HUD
      hud.setObjective(routine.objective());
      hud.setWatchTime(routine.clockString(), routine.watchLabel());

      // Light flicker scales with dread
      const flick = 1 - manager.dread * (0.3 + 0.4 * Math.abs(Math.sin(now * 0.02)));
      for (const l of world.flickerLights) {
        (l as THREE.PointLight).intensity = ((l as THREE.PointLight).userData.base ??=
          (l as THREE.PointLight).intensity) * flick;
      }

      // Win: walked out through the exit door once the routine is complete
      if (
        routine.canLeave() &&
        pos.z > world.exitZone.minZ - 0.6 &&
        pos.x > world.exitZone.minX &&
        pos.x < world.exitZone.maxX
      ) {
        win();
      }
    }

    renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

main();
